// Build-time guide loader using Vite's glob import with multi-language support
const allMarkdownFiles = import.meta.glob('../../docs/guides/**/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
});

interface Guide {
  order: number;
  slug: string;
  title: string;
  content: string;
  language: string;
}

interface GuidesByLanguage {
  [language: string]: Guide[];
}

// Extract and process guide data from imported markdown files
const guidesByLanguage: GuidesByLanguage = {};

Object.entries(allMarkdownFiles).forEach(([path, content]) => {
  // Extract language from path: docs/guides/en/01-getting-started.md -> en
  const pathParts = path.split('/');
  const languageIndex = pathParts.findIndex(part => part === 'guides') + 1;
  const language = pathParts[languageIndex] || 'en';

  const fileName = path.split('/').pop()!.replace('.md', '');
  const [orderStr, ...slugParts] = fileName.split('-');
  const order = Number(orderStr);
  const slugRaw = slugParts.join('-');

  // Convert slug to title (replace hyphens with spaces and capitalize)
  const title = slugRaw
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const guide: Guide = {
    order,
    slug: slugRaw,
    title,
    content: content as string,
    language,
  };

  if (!guidesByLanguage[language]) {
    guidesByLanguage[language] = [];
  }

  guidesByLanguage[language].push(guide);
});

// Sort guides within each language by order
Object.keys(guidesByLanguage).forEach(language => {
  guidesByLanguage[language].sort((a, b) => a.order - b.order);
});

/**
 * Get guides for a specific language with fallback to English
 * @param language - The desired language code (e.g., 'en', 'es', 'de', 'pt')
 * @returns Array of guides in the requested language, or English if not available
 */
export function getGuidesForLanguage(language: string = 'en'): Guide[] {
  // First try to get guides in the requested language
  if (guidesByLanguage[language] && guidesByLanguage[language].length > 0) {
    return guidesByLanguage[language];
  }

  // Fallback to English if the requested language is not available
  return guidesByLanguage['en'] || [];
}

/**
 * Check if guides are available in a specific language
 * @param language - The language code to check
 * @returns boolean indicating if guides exist for that language
 */
export function isLanguageAvailable(language: string): boolean {
  return Boolean(guidesByLanguage[language] && guidesByLanguage[language].length > 0);
}

/**
 * Get all available languages that have documentation
 * @returns Array of language codes that have guides
 */
export function getAvailableLanguages(): string[] {
  return Object.keys(guidesByLanguage).filter(lang => guidesByLanguage[lang].length > 0);
}

/**
 * Get guide metadata for language selection UI
 * @returns Object with language availability information
 */
export function getLanguageMetadata(): Record<string, { available: boolean; guideCount: number }> {
  const metadata: Record<string, { available: boolean; guideCount: number }> = {};

  // Include all possible languages, even if they don't have content yet
  const allLanguages = ['en', 'es', 'de', 'pt'];

  allLanguages.forEach(lang => {
    metadata[lang] = {
      available: isLanguageAvailable(lang),
      guideCount: guidesByLanguage[lang]?.length || 0,
    };
  });

  return metadata;
}

// Export default guides (English) for backward compatibility
const guides = getGuidesForLanguage('en');
export default guides;

// Export the multi-language functionality
export { guidesByLanguage, type Guide, type GuidesByLanguage };
